import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * MobileNav (Feb 2026, updated)
 *  - `links` (e.g. "Get Instant Pricing") render inline on desktop and inside
 *    the hamburger overlay on mobile.
 *  - `actions` (Login / Register / Dashboard / Logout) ALWAYS render inline —
 *    on mobile they're shown in a compact form right next to the hamburger so
 *    users never lose access to auth CTAs.
 */
const MobileNav = ({ links, actions }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Desktop nav — full layout, hidden on mobile */}
      <nav className="hidden md:flex items-center gap-6 lg:gap-8" data-testid="desktop-nav">
        {links.map((link, i) => (
          <Link key={i} to={link.to} className={link.className} data-testid={link.testId}>
            {link.label}
          </Link>
        ))}
        <LanguageSwitcher />
        {actions.map((action, i) => (
          action.to ? (
            <Link key={i} to={action.to} className={action.className} data-testid={action.testId}>
              {action.label}
            </Link>
          ) : (
            <button key={i} onClick={action.onClick} className={action.className} data-testid={action.testId}>
              {action.label}
            </button>
          )
        ))}
      </nav>

      {/* Mobile cluster — compact actions + hamburger */}
      <div className="flex md:hidden items-center gap-1.5" data-testid="mobile-nav-cluster">
        {actions.map((action, i) => {
          // Distinguish primary action (Register / Dashboard) — solid filled style;
          // others (Login / Logout) — compact text link.
          const isPrimary = (action.className || '').includes('bg-brand-primary');
          const compactCls = isPrimary
            ? 'bg-brand-primary text-sand-50 rounded-none px-3 py-1.5 text-[11px] font-bold tracking-[0.1em] uppercase hover:bg-[#A84A38] transition-colors'
            : 'text-[11px] font-bold tracking-[0.1em] uppercase text-text-secondary hover:text-text-primary transition-colors px-2 py-1.5';
          return action.to ? (
            <Link key={i} to={action.to} className={compactCls}
              data-testid={`mobile-inline-${action.testId}`}>
              {action.label}
            </Link>
          ) : (
            <button key={i} onClick={action.onClick} className={compactCls}
              data-testid={`mobile-inline-${action.testId}`}>
              {action.label}
            </button>
          );
        })}
        <button
          onClick={() => setOpen(!open)}
          className="p-1.5 text-text-primary border border-[#E8E1D5] rounded-none ml-0.5"
          data-testid="mobile-menu-btn"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
      </div>

      {/* Mobile menu overlay — links + language switcher only */}
      {open && (
        <div className="md:hidden fixed inset-0 top-[60px] bg-sand-50 z-50 border-t border-[#E8E1D5] overflow-y-auto" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-4">
            {links.map((link, i) => (
              <Link key={i} to={link.to} onClick={() => setOpen(false)}
                className="text-base tracking-[0.15em] uppercase font-bold text-text-secondary py-2 border-b border-[#E8E1D5]"
                data-testid={`mobile-${link.testId}`}>
                {link.label}
              </Link>
            ))}
            {/* Duplicate actions inside the overlay too, in case the inline cluster
                is hard to tap on very small devices. */}
            {actions.map((action, i) => (
              action.to ? (
                <Link key={i} to={action.to} onClick={() => setOpen(false)}
                  className={`text-base tracking-[0.15em] uppercase font-bold py-2 border-b border-[#E8E1D5] ${
                    (action.className || '').includes('bg-brand-primary')
                      ? 'text-brand-primary' : 'text-text-secondary'
                  }`}
                  data-testid={`mobile-${action.testId}`}>
                  {action.label}
                </Link>
              ) : (
                <button key={i} onClick={() => { action.onClick(); setOpen(false); }}
                  className="text-base tracking-[0.15em] uppercase font-bold text-text-secondary py-2 border-b border-[#E8E1D5] text-left"
                  data-testid={`mobile-${action.testId}`}>
                  {action.label}
                </button>
              )
            ))}
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;
